import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Stack,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import RefreshIcon        from "@mui/icons-material/Refresh";
import ManageSearchIcon   from "@mui/icons-material/ManageSearch";
import OpenInNewIcon      from "@mui/icons-material/OpenInNew";
import PsychologyAltIcon  from "@mui/icons-material/PsychologyAlt";
import FolderSpecialIcon  from "@mui/icons-material/FolderSpecial";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import dayjs from "dayjs";
import utc      from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useNavigate } from "react-router-dom";
import API from "../../../../api";
import { inventoryIpUrl } from "../../../../components/ImportantFunctions";

dayjs.extend(utc);
dayjs.extend(timezone);

const SFP_TYPES = ["LECS-CORE-SFP", "LECS-CORE-4-PRO", "LECS-CORE-SFP-PLUS"];

const ALL_SEVERITY_TYPES = [
  { key: "critical",  label: "Critical",           color: "#931A1D" },
  { key: "high",      label: "High",               color: "#FEA51D" },
  { key: "alert",     label: "Alert",              color: "#D1C167" },
  { key: "movements", label: "Movements",          color: "#459296" },
  { key: "threat",    label: "Threat Intelligence",color: "#C485BF" },
];

const ALL_SEVERITY_SUMMARY = [
  { key: "critical",  label: "Crit", color: "#931A1D" },
  { key: "high",      label: "High", color: "#FEA51D" },
  { key: "alert",     label: "Alert",color: "#D1C167" },
  { key: "movements", label: "Mov",  color: "#459296" },
  { key: "threat",    label: "Thr",  color: "#C485BF" },
];

const BEHAVIOR_SEVERITY_ORDER = ["high", "medium", "low", "info"];

const PRESETS = [
  { label: "1H",  hours: 1  },
  { label: "6H",  hours: 6  },
  { label: "24H", hours: 24 },
  { label: "7D",  days:  7  },
];

const topSeverityColor = (group) => {
  if (group.critical  > 0) return "#931A1D";
  if (group.high      > 0) return "#FEA51D";
  if (group.alert     > 0) return "#D1C167";
  if (group.movements > 0) return "#459296";
  if (group.threat    > 0) return "#C485BF";
  return "#444";
};

const StatCard = ({ label, count, color }) => (
  <Grid size={{ xs: 6, sm: 4, md: 2 }}>
    <Paper elevation={2} sx={{
      p: 1.5, backgroundColor: "#1a1a1a",
      border: `1px solid ${color}55`, borderLeft: `4px solid ${color}`,
      textAlign: "center",
    }}>
      <Typography variant="h5" fontWeight={800} sx={{ color }}>{count}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  </Grid>
);

// ── Flat host row ──────────────────────────────────────────────
// Chiavi in maiuscolo: la risposta di getOtSecurityByIp usa
// CRITICAL/HIGH/MEDIUM/LOW (vedi ot-security-events-ctrl.mjs), non le
// versioni minuscole — prima il lookup sotto falliva sempre e il
// breakdown per severità nel tooltip non compariva mai.
const OT_CLASS_COLORS = { CRITICAL: "#db4f4a", HIGH: "#e2724e", MEDIUM: "#f0c050", LOW: "#4cceac" };

const HostRow = ({ group, onOpenContextLog, dateFrom, dateTo, isSfp }) => {
  const SEVERITY_SUMMARY = isSfp
    ? ALL_SEVERITY_SUMMARY.filter((s) => s.key !== "movements")
    : ALL_SEVERITY_SUMMARY;
  const navigate     = useNavigate();
  const accentColor  = topSeverityColor(group);
  const hasBehaviors = (group.behaviors?.length ?? 0) > 0;
  const hasAggs      = (group.aggregations?.length ?? 0) > 0;
  const hasOtSec     = group.otSec != null && group.otSec.count > 0;
  const count        = group.count ?? 0;

  const handleOpenIP = (e) => {
    e.stopPropagation();
    navigate(inventoryIpUrl(group.ip));
  };

  return (
    <Box sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      flexWrap: "wrap",
      px: 2,
      py: 1,
      backgroundColor: "#1a1a1a",
      border: "1px solid #2a2a2a",
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: "4px",
      mb: 0.75,
    }}>
      {/* IP + action icons */}
      <Box display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 160 }}>
        <Typography sx={{
          fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem",
          color: "#e8e8e8", letterSpacing: "0.02em", whiteSpace: "nowrap",
        }}>
          {group.ip}
        </Typography>
        <Tooltip title="Host Details">
          <IconButton size="small" onClick={handleOpenIP} sx={{ color: "#666", p: 0.3, "&:hover": { color: "#43B4B9" } }}>
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open Context Log">
          <IconButton
            size="small"
            onClick={() => onOpenContextLog?.(group.ip, dateFrom, dateTo)}
            sx={{ color: "#666", p: 0.3, "&:hover": { color: "#43B4B9" } }}
          >
            <ManageSearchIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Severity breakdown */}
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {SEVERITY_SUMMARY.filter((s) => group[s.key] > 0).map((s) => (
          <Tooltip key={s.key} title={s.label}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: 0.4,
              px: 0.75, py: 0.2, borderRadius: "4px",
              backgroundColor: `${s.color}18`, border: `1px solid ${s.color}44`,
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "2px", backgroundColor: s.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: s.color, fontVariantNumeric: "tabular-nums" }}>
                {group[s.key]}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>

      {/* Tiresia behaviors */}
      {hasBehaviors && (
        <Tooltip title={`${group.behaviors.length} Tiresia behaviors`}>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.4,
            px: 0.75, py: 0.2, borderRadius: "4px",
            backgroundColor: "#7C4DFF15", border: "1px solid #7C4DFF40",
          }}>
            <PsychologyAltIcon sx={{ fontSize: 12, color: "#7C4DFF" }} />
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#7C4DFF" }}>
              {group.behaviors.length}
            </Typography>
          </Box>
        </Tooltip>
      )}

      {/* Aggregation cases */}
      {hasAggs && (
        <Tooltip title={`${group.aggregations.length} correlation cases`}>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.4,
            px: 0.75, py: 0.2, borderRadius: "4px",
            backgroundColor: "#E67E2215", border: "1px solid #E67E2240",
          }}>
            <FolderSpecialIcon sx={{ fontSize: 12, color: "#E67E22" }} />
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#E67E22" }}>
              {group.aggregations.length}
            </Typography>
          </Box>
        </Tooltip>
      )}

      {/* OT Security events */}
      {hasOtSec && (
        <Tooltip title={
          <Box>
            <Typography variant="caption" display="block" fontWeight={700} mb={0.5}>OT Security events</Typography>
            {["CRITICAL","HIGH","MEDIUM","LOW"].map((cls) => group.otSec[cls] > 0 && (
              <Typography key={cls} variant="caption" display="block" sx={{ color: OT_CLASS_COLORS[cls] }}>
                {cls.charAt(0) + cls.slice(1).toLowerCase()}: {group.otSec[cls]}
              </Typography>
            ))}
          </Box>
        }>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.4,
            px: 0.75, py: 0.2, borderRadius: "4px",
            backgroundColor: "#db4f4a15", border: "1px solid #db4f4a40",
          }}>
            <PrecisionManufacturingIcon sx={{ fontSize: 12, color: "#db4f4a" }} />
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#db4f4a" }}>
              {group.otSec.count}
            </Typography>
          </Box>
        </Tooltip>
      )}

      {/* Total events — pushed right */}
      <Typography sx={{ fontSize: "0.72rem", color: "#888", fontVariantNumeric: "tabular-nums", ml: "auto", whiteSpace: "nowrap" }}>
        {count.toLocaleString()} events
      </Typography>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════
const IncidentMapTab = ({ onOpenContextLog, otEnabled = true }) => {
  const tz    = sessionStorage.getItem("timezone") || "Europe/Amsterdam";
  const isSfp = SFP_TYPES.includes(sessionStorage.getItem("tipoLecs"));
  const SEVERITY_TYPES = isSfp
    ? ALL_SEVERITY_TYPES.filter((s) => s.key !== "movements")
    : ALL_SEVERITY_TYPES;
  const [groups,   setGroups]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [sortBy,   setSortBy]   = useState("critical");
  const [dateFrom, setDateFrom] = useState(dayjs().tz(tz).subtract(24, "hour"));
  const [dateTo,   setDateTo]   = useState(dayjs().tz(tz));

  // Se l'utente clicca due preset di seguito (o "Apply" due volte) prima che
  // il primo risponda, la richiesta precedente non deve poter sovrascrivere
  // i risultati di quella più recente.
  const activeFetchRef = useRef(null);

  useEffect(() => {
    return () => activeFetchRef.current?.abort();
  }, []);

  const fetchData = useCallback((from, to) => {
    activeFetchRef.current?.abort();
    const abortCtrl = new AbortController();
    activeFetchRef.current = abortCtrl;

    setLoading(true);
    const timeFilter = [
      dayjs(from).utc().format("YYYY-MM-DD HH:mm"),
      dayjs(to).utc().format("YYYY-MM-DD HH:mm"),
    ];

    API.getIncidentsByIp(
      { timeFilter, timezone: tz, hideSuppressed: true, hideBackup: true },
      abortCtrl.signal
    )
      .catch(() => ({ data: { data: [] } }))
      .then((res) => {
        if (abortCtrl.signal.aborted) return;

        const newGroups = (res.data?.data ?? []).map((row) => ({
          ip:        row.src_ip,
          count:     row.total      ?? 0,
          critical:  row.critical   ?? 0,
          high:      row.high       ?? 0,
          alert:     row.alert      ?? 0,
          movements: row.movements  ?? 0,
          threat:    row.threat     ?? 0,
          behaviors:           [],
          topBehaviorSeverity: null,
          aggregations:        [],
          otSec:               null,   // { count, critical, high, medium, low }
        }));

        setGroups(newGroups);
        setLoading(false);

        // Non-blocking enrichment
        const hoursRange = Math.max(1, Math.ceil(dayjs().diff(dayjs(from), "hour", true)));
        const daysDiff   = dayjs(to).diff(dayjs(from), "day", true);
        const aggPromise = daysDiff <= 7
          ? API.getAggregations({
              aggregation_dimension: "host",
              timestamp: { dateFrom: dayjs(from).toISOString(), dateTo: dayjs(to).toISOString() },
            }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] });

        const otByIpPromise = otEnabled
          ? API.getOtSecurityByIp({
              dateFrom: dayjs(from).toISOString(),
              dateTo:   dayjs(to).toISOString(),
            }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] });

        Promise.all([
          API.getTiresiaAnomalies({ hours: hoursRange }).catch(() => ({ data: { data: [] } })),
          aggPromise,
          otByIpPromise,
        ]).then(([behavRes, aggRes, otByIpRes]) => {
          if (abortCtrl.signal.aborted) return;

          const behaviorsByIp = {};
          (behavRes.data?.data ?? []).forEach((a) => {
            const ip = a.entity?.id;
            if (!ip || ip === "_global_") return;
            if (!behaviorsByIp[ip]) behaviorsByIp[ip] = [];
            behaviorsByIp[ip].push(a);
          });
          const getTopSeverity = (list) => {
            for (const s of BEHAVIOR_SEVERITY_ORDER) {
              if (list.some((a) => a.severity_label === s)) return s;
            }
            return null;
          };

          const aggList = Array.isArray(aggRes.data) ? aggRes.data : (aggRes.data?.data ?? []);
          const aggByIp = {};
          aggList.forEach((c) => {
            const ip = c.host ?? c.group_values?.host;
            if (!ip) return;
            if (!aggByIp[ip]) aggByIp[ip] = [];
            aggByIp[ip].push(c);
          });

          const otByIp = {};
          (Array.isArray(otByIpRes.data) ? otByIpRes.data : []).forEach((row) => {
            if (row.ip) otByIp[row.ip] = row;
          });

          setGroups((prev) =>
            prev.map((g) => ({
              ...g,
              behaviors:           behaviorsByIp[g.ip] ?? [],
              topBehaviorSeverity: getTopSeverity(behaviorsByIp[g.ip] ?? []),
              aggregations:        aggByIp[g.ip] ?? [],
              otSec:               otByIp[g.ip] ?? null,
            }))
          );
        });
      });
  }, [tz, otEnabled]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only fetch of the initial range; dateFrom/dateTo changes are already handled explicitly by applyPreset calling fetchData, so adding them here would trigger duplicate fetches
  useEffect(() => { fetchData(dateFrom, dateTo); }, []);

  const applyPreset = (preset) => {
    const to   = dayjs().tz(tz);
    const from = preset.hours
      ? dayjs().tz(tz).subtract(preset.hours, "hour")
      : dayjs().tz(tz).subtract(preset.days, "day");
    setDateFrom(from);
    setDateTo(to);
    fetchData(from, to);
  };

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    setPage(0);
    return [...groups].sort((a, b) => {
      if (sortBy === "critical") {
        if (b.critical !== a.critical) return b.critical - a.critical;
        if (b.high     !== a.high)     return b.high     - a.high;
        return b.count - a.count;
      }
      if (sortBy === "ip") return a.ip.localeCompare(b.ip);
      return b.count - a.count;
    });
  }, [groups, sortBy]);

  const pageCount  = Math.ceil(sorted.length / PAGE_SIZE);
  const pageSlice  = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const totals = useMemo(
    () => SEVERITY_TYPES.reduce(
      (acc, s) => ({ ...acc, [s.key]: groups.reduce((sum, g) => sum + (g[s.key] || 0), 0) }),
      {}
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- SEVERITY_TYPES is a new array reference every render (derived from isSfp); adding it would recompute this memo every render, defeating its purpose. Its actual values only change when isSfp (sessionStorage) changes, which requires a remount.
    [groups]
  );

  const otSecTotal = useMemo(
    () => groups.reduce((sum, g) => sum + (g.otSec?.count ?? 0), 0),
    [groups]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>

        {/* ── Date filter ─────────────────────────────────────── */}
        <Paper elevation={2} sx={{ p: 2, backgroundColor: "#1e1e1e", border: "1px solid #333" }}>
          <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            <Typography variant="subtitle2" color="#459095" fontWeight={700} sx={{ mr: 1 }}>
              Time Range
            </Typography>
            {PRESETS.map((p) => (
              <Chip key={p.label} label={p.label} size="small" onClick={() => applyPreset(p)}
                sx={{
                  cursor: "pointer",
                  backgroundColor: "rgba(69,144,149,0.15)", color: "#43B4B9",
                  border: "1px solid #459095", fontWeight: 700,
                  "&:hover": { backgroundColor: "rgba(69,144,149,0.3)" },
                }}
              />
            ))}
            <DateTimePicker label="From" value={dateFrom} onChange={(v) => v && setDateFrom(v)} ampm={false}
              slotProps={{ textField: { size: "small", sx: { minWidth: 190 } } }} />
            <DateTimePicker label="To" value={dateTo} onChange={(v) => v && setDateTo(v)} ampm={false}
              slotProps={{ textField: { size: "small", sx: { minWidth: 190 } } }} />
            <Button variant="contained" size="small"
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
              onClick={() => fetchData(dateFrom, dateTo)} disabled={loading}
              sx={{ backgroundColor: "#459095", "&:hover": { backgroundColor: "#367a7e" } }}
            >
              Apply
            </Button>
            <Typography variant="body2" sx={{ ml: "auto", color: "#888", whiteSpace: "nowrap" }}>
              {groups.length} hosts
            </Typography>
          </Box>
        </Paper>

        {/* ── Stat cards ──────────────────────────────────────── */}
        <Grid container spacing={1} alignItems="stretch">
          {SEVERITY_TYPES.map((s) => (
            <StatCard key={s.key} label={s.label} count={totals[s.key] ?? 0} color={s.color} />
          ))}
          {otEnabled && <StatCard label="OT events" count={otSecTotal} color="#db4f4a" />}
        </Grid>

        {/* ── Sort + pagination controls ───────────────────────── */}
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sort by</InputLabel>
            <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)}>
              <MenuItem value="count">Total incidents ↓</MenuItem>
              <MenuItem value="critical">Critical first ↓</MenuItem>
              <MenuItem value="ip">Host IP (A-Z)</MenuItem>
            </Select>
          </FormControl>
          {pageCount > 1 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
                sx={{ color: "#459095", "&.Mui-disabled": { color: "#444" } }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
              </IconButton>
              <Typography sx={{ fontSize: 12, color: "text.secondary", minWidth: 64, textAlign: "center" }}>
                {page + 1} / {pageCount}
              </Typography>
              <IconButton size="small" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)}
                sx={{ color: "#459095", "&.Mui-disabled": { color: "#444" } }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
              </IconButton>
            </Box>
          )}
        </Box>

        {loading && (
          <Stack spacing={0.75}>
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={44} sx={{ bgcolor: "rgba(255,255,255,0.07)", borderRadius: "4px" }} />
            ))}
          </Stack>
        )}

        {!loading && sorted.length === 0 && (
          <Paper elevation={0} sx={{ p: 4, backgroundColor: "#1e1e1e", border: "1px dashed #444", borderRadius: 2, textAlign: "center" }}>
            <Typography color="text.secondary">No incidents found in the selected time range.</Typography>
          </Paper>
        )}

        {!loading && pageSlice.map((group) => (
          <HostRow
            key={group.ip}
            group={group}
            onOpenContextLog={onOpenContextLog}
            dateFrom={dateFrom}
            dateTo={dateTo}
            isSfp={isSfp}
          />
        ))}
      </Box>
    </LocalizationProvider>
  );
};

export default IncidentMapTab;